import clsx from 'clsx'
import { connect } from 'react-redux'
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import Switch from 'LuxKit/Switch/Switch'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import { useAppDispatch } from 'hooks/reduxHooks'
import { updateUserPreferenceDestination, deleteUserPreferenceDestination } from 'actions/userTravelPreferencesActions'
import ModalContext from 'contexts/ModalContext'
import AddDestinationToggleModal from './AddDestinationToggleModal'
import Clickable from 'components/Common/Clickable'
import useQueryParams from 'hooks/useQueryParams'

interface MappedStateProps {
  savedDestinations?: Array<App.UserPreferenceDestination>
  fetchingTravelPreferences: boolean
}

interface Props {
  placeId: string
  userId: string
  className?: string
}

function HotelSearchAddDestinationToggle(props: Props & MappedStateProps) {
  const { savedDestinations, userId, placeId, fetchingTravelPreferences, className } = props
  const intl = useIntl()
  const isDestinationSaved = useMemo(() => !!savedDestinations?.find(destination => destination.placeId === placeId), [savedDestinations, placeId])

  const dispatch = useAppDispatch()
  const [pendingSnackbar, setPendingSnackbar] = useState<boolean>(false)
  const queryParams = useQueryParams()

  const handleClick = useCallback<React.MouseEventHandler<HTMLInputElement>>(() => {
    if (!isDestinationSaved) {
      dispatch(updateUserPreferenceDestination(userId, placeId))
      setPendingSnackbar(true)
    } else {
      dispatch(deleteUserPreferenceDestination(userId, placeId))
    }
  }, [isDestinationSaved, dispatch, userId, placeId])

  const snackbarMessage = intl.formatMessage({
    id: 'L5EvMfSH69',
    defaultMessage: 'You can view and update your destinations.',
    description: 'Snackbar message shown after saving a destination on the hotel search page',
  })

  const snackbarHeading = intl.formatMessage({
    id: 'QDtA6RmQxl',
    defaultMessage: 'Destination saved',
    description: 'Snackbar heading shown after saving a destination on the hotel search page',
  })

  const editPreferencesText = intl.formatMessage({
    id: 'O6PnJ0l0N9',
    defaultMessage: 'Edit preferences',
    description: 'Link text to edit travel preferences on the user account settings page',
  })

  const addedToWatchlistText = intl.formatMessage({
    id: 'zGDmD+OUap',
    defaultMessage: 'Added to Watchlist',
    description: 'Label shown when destination is saved to watchlist',
  })

  const addToWatchlistText = intl.formatMessage({
    id: 'uNJj1hFqwd',
    defaultMessage: 'Add to Watchlist',
    description: 'Label shown to add destination to watchlist on the hotel search page',
  })

  useEffect(() => {
    if (!fetchingTravelPreferences && pendingSnackbar && isDestinationSaved) {
      showSnackbar(snackbarMessage, 'success', {
        heading: snackbarHeading,
        action: {
          label: editPreferencesText,
          to: '/account/travel-preferences#destinations',
        },
      })
      setPendingSnackbar(false)
    }
  }, [fetchingTravelPreferences, pendingSnackbar, isDestinationSaved, snackbarMessage, snackbarHeading, editPreferencesText])

  const showModal = useContext(ModalContext)
  const destinationName = queryParams.get('destinationName')
  const displayModal = useCallback(() => {
    if (destinationName) {
      showModal(<AddDestinationToggleModal placeId={placeId} placeName={destinationName} onAddDestination={handleClick} destinationSaved={isDestinationSaved} />)
    }
  }, [showModal, handleClick, placeId, isDestinationSaved, destinationName])

  return (
    <Group className={clsx(className)} direction="horizontal" horizontalAlign="space-between" verticalAlign="center" gap={8}>
      <Group direction="horizontal" gap={4} verticalAlign="center" horizontalAlign="start">
        <BodyText variant="medium">{isDestinationSaved ? addedToWatchlistText : addToWatchlistText}</BodyText>
        <Clickable onClick={displayModal}>
          <LineInfoCircleIcon size="S" />
        </Clickable>
      </Group>
      <Switch checked={isDestinationSaved} onClick={handleClick} />
    </Group>
  )
}

export default connect<MappedStateProps, undefined, Props, App.State>((appState) => {
  return {
    fetchingTravelPreferences: !!appState.userTravelPreferences.destinations.fetching,
    savedDestinations: appState.userTravelPreferences.destinations.data,
  }
})(HotelSearchAddDestinationToggle)
